import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, SimpleChanges } from "@angular/core";
import { BehaviorSubject, filter, interval, Observable, ReplaySubject, switchMap, takeUntil } from "rxjs";
import { SpxLaunch } from "../../data/spx-launch";
import { MissionService } from "../../services/mission-service";

type JustifyContent = "left" | "center" | "right";

const SECONDS_IN_A_MINUTE: number = 60;
const SECONDS_IN_AN_HOUR: number = 3600;
const SECONDS_IN_A_DAY: number = 86400;
const HOURS_IN_A_DAY: number = 24;
const DIGIT_HEIGHT: number = -13;

@Component({
  selector: "launch-countdown",
  templateUrl: "./launch-countdown.component.html",
  styleUrls: ["./launch-countdown.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LaunchCountdownComponent {
  @Input() public shouldBlink: boolean = false;
  @Input() public justify: JustifyContent;
  @Input() public missionId: string;

  private readonly _destroy$ = new ReplaySubject<void>(1);
  private _missionId$: BehaviorSubject<string> = new BehaviorSubject(null);
  readonly missionId$: Observable<string> = this._missionId$.asObservable();

  private launch: SpxLaunch = null;

  secondsOnesTop: number = 0;
  secondsTensTop: number = 0;
  minutesOnesTop: number = 0;
  minutesTensTop: number = 0;
  hoursOnesTop: number = 0;
  hoursTensTop: number = 0;
  daysOnesTop: number = 0;
  daysTensTop: number = 0;
  daysHundredsTop: number = 0;
  isUpcoming: boolean = true;
  isInitialized: boolean = false;


  constructor(private missionService: MissionService, private zone: NgZone, private cd: ChangeDetectorRef) {
    this.cd.detach();
  }

  ngOnInit() {
    this.missionId$.pipe(
      filter(id => !!id),
      switchMap(id => this.missionService.getTileForMission(this.missionId)),
      takeUntil(this._destroy$)
    ).subscribe(launch => {
      this.launch = launch;
      this._setDigits();
      this.isInitialized = true;
    });

    this.zone.runOutsideAngular(() =>
      interval(1000).pipe(takeUntil(this._destroy$)).subscribe(_ => {
        this._setDigits();
        this.cd.detectChanges();
      })
    );

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.missionId && changes.missionId.currentValue) {
      this._missionId$.next(changes.missionId.currentValue);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _setDigits(): void {
    if (!this.launch) {
      return;
    }

    const count: number = this._getCount(this.launch);
    const countAbs = Math.abs(count);
    const seconds: number = countAbs % SECONDS_IN_A_MINUTE;
    const minutes: number = Math.floor(countAbs / SECONDS_IN_A_MINUTE) % SECONDS_IN_A_MINUTE;
    const hours: number = Math.floor(countAbs / SECONDS_IN_AN_HOUR) % HOURS_IN_A_DAY;
    const days: number = Math.floor(countAbs / SECONDS_IN_A_DAY);

    this.secondsOnesTop = (seconds % 10) * DIGIT_HEIGHT;
    this.secondsTensTop = Math.floor(seconds / 10) * DIGIT_HEIGHT;
    this.minutesOnesTop = (minutes % 10) * DIGIT_HEIGHT;
    this.minutesTensTop = Math.floor(minutes / 10) * DIGIT_HEIGHT;
    this.hoursOnesTop = (hours % 10) * DIGIT_HEIGHT;
    this.hoursTensTop = Math.floor(hours / 10) * DIGIT_HEIGHT;
    this.daysOnesTop = (days % 10) * DIGIT_HEIGHT;
    this.daysTensTop = (Math.floor(days / 10) % 10) * DIGIT_HEIGHT;
    this.daysHundredsTop = Math.floor(days / 100) * DIGIT_HEIGHT;

    if (count < 0 && !this.isUpcoming) {
      this.isUpcoming = true;
    }

    if (count >= 0 && this.isUpcoming) {
      this.isUpcoming = false;
    }
  }

  private _getCount(launch: SpxLaunch): number {
    // If there's an override with date/time, countdown to that
    if (launch.override?.date && launch.override?.time) {
      const overrideDateTime = this.missionService.getLaunchTimeWithTimezone(
        `${launch.override.date}T${launch.override.time}`, launch.launchSite);
      return Math.round((Date.now() - overrideDateTime.getTime()) / 1000);
    }

    if (launch.override?.windowOpenDate && launch.override?.windowOpenTime) {
      const overrideDateTime = this.missionService.getLaunchTimeWithTimezone(
        `${launch.override.windowOpenDate}T${launch.override.windowOpenTime}`, launch.launchSite);
      return Math.round((Date.now() - overrideDateTime.getTime()) / 1000);
    }

    return launch.timings && (launch.timings.TZeroLaunchDate || launch.timings.PrimaryLaunchDate) ?
      launch.timings.TZeroLaunchDate ?
        launch.timings.TZeroPaused ?
          launch.timings.TZeroValue :
          Math.round((Date.now() / 1000) - launch.timings.TZeroLaunchDate.Seconds)
        : Math.round((Date.now() / 1000) - launch.timings.PrimaryLaunchDate.Seconds)
      :
      launch.launchDate && launch.launchTime ?
        Math.round((Date.now() - this._getLaunchedTime(launch)) / 1000)
        : 0;
  }

  // Number of milliseconds between epoch and a past launch given in local time, accounting for DST.
  private _getLaunchedTime(launch: SpxLaunch): number {
    return this.missionService.getLaunchTimeWithTimezone(
      `${launch.launchDate}T${launch.launchTime}`, launch.launchSite).getTime();
  }
}
